import React, { useEffect, useMemo, useState } from "react";
import LightThemePage from "../../components/layout/LightThemePage";
import {
  Box,
  Card,
  CardContent,
  Container,
  Divider,
  Stack,
} from "@mui/material";
import Header from "../../components/dashboard/Header";
import { useParams, useSearchParams } from "react-router-dom";
import { useExam } from "../../hooks/useExam";
import { isEmpty, minBy, toNumber, uniqBy } from "lodash";
import TrackViewSkeleton from "../../components/tracks/TrackViewSkeleton";
import NotFound from "../../components/global/NotFound";
import axios, { AxiosResponse } from "axios";
import { useMutation } from "react-query";
import { TTransposedTraining } from "../../shared/types/transposedTraining.type";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import { TrainingSetting } from "../../shared/types/trainingSetting.type";
import InformationSection from "../../components/exams/InformationSection";
import KeyParamSection from "../../components/exams/KeyParamSection";
import GaitAndHrZone from "../../components/exams/GaitAndHrZone";
import MapSection from "../../components/exams/MapSection";
import { CheckPoint } from "../../shared/types/training.type";
import ChartsSection from "../../components/exams/ChartsSection";
import { Scale } from "./ExamView";
import CheckpointsSection from "../../components/exams/CheckpointsSection";
import { useAuth } from "../../hooks/useAuth";
import { mergeArr } from "../../shared/utils/MapSection";
import { formatDate } from "../../shared/utils/DateUtils";
import { routes } from "../routes";
import CommentSection from "../../components/exams/CommentSection";
import "./externalTraining.css";
import useHubspotTracking from "../../hooks/useHubspotTracking";
import { Chukkers } from "../../shared/types/chukkers.type";
import { PrintPage } from "./PrintPage";
import { Print } from "@mui/icons-material";

function ExternalTraining() {
  let { id } = useParams();
  const { exam, isExamLoading, examHasError, examError, examQuery } = useExam(
    false,
    null,
    toNumber(id),
  );
  const transposedMutation = useMutation(
    (): Promise<AxiosResponse<TTransposedTraining>> =>
      axios.get(exam?.training?.moments_transposed_file_url as string),
  );
  const userSettings = useSelector<RootState>(
    (state) => state.user.user?.training_settings,
  ) as TrainingSetting;
  const isGMapLoaded = useSelector<RootState>(
    (state) => state.display.mapLoaded,
  ) as boolean;
  let [searchParams, setSearchParams] = useSearchParams();
  const { setToken, getUser, isUserFetching, auth } = useAuth();

  //To hide chat banner in this page
  useHubspotTracking();

  //State
  const [chukkers, setChukkers] = useState<Chukkers>([]);
  const [distance, setDistance] = useState<string>("0 mi");
  const [speed, setSpeed] = useState<number[]>([]);
  const [bpm, setBpm] = useState<number[]>([]);
  const [loCardio, setLoCardio] = useState<number[]>([]);
  const [time, setTime] = useState<number[]>([]);
  const [rawDistance, setDistanceRaw] = useState<number[]>([]);
  const [strideFrequency, setStrideFrequency] = useState<number[]>([]);
  const [strideLength, setStrideLength] = useState<number[]>([]);
  const [currentIndex, setCurrentindex] = useState(0);
  const [allure, setAllure] = useState<number[]>([]);
  const [lats, setLats] = useState<number[]>([]);
  const [lngs, setLangs] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);
  const [scaleOption, setScaleOption] = useState<Scale>("DISTANCE");

  const RESTRICTED = true;

  const isFinishLineActive = exam?.training?.use_finish_line ?? false;

  //query params
  const TOKEN = searchParams.get("token");
  //Effect

  useEffect(() => {
    //if token defined set
    if (TOKEN) {
      setToken(TOKEN);
      getUser();
    }
  }, [TOKEN]);

  useEffect(() => {
    if (exam) {
      //If the training has no horse we display the edit modal and prevent the user from leaving it

      if (auth?.type === "POLO") {
        setChukkers(exam.training?.chukkers || []);
      }

      let examGoodGps = exam?.training?.percentage_good_gps;

      if (
        typeof examGoodGps === "number" &&
        Number.isFinite(examGoodGps) &&
        examGoodGps < 20
      ) {
        setScaleOption("TIME");
      }
    }
  }, [exam?.["@id"], auth?.type]);

  useEffect(() => {
    if (exam?.training?.moments_transposed_file_url) {
      transposedMutation
        .mutateAsync()
        .then((res) => {
          let lts = sliceForRelevantMoment(res?.data.latitude ?? []);
          let lgs = sliceForRelevantMoment(res?.data.longitude ?? []);

          setLats(lts);
          setLangs(lgs);
          setSpeed(sliceForRelevantMoment(res?.data?.speed ?? []));
          setBpm(sliceForRelevantMoment(res?.data?.bpm ?? []));
          setDistanceRaw(sliceForRelevantMoment(res.data?.distance ?? []));
          setTime(sliceForRelevantMoment(res.data?.timestamp ?? []));
          setStrideFrequency(sliceForRelevantMoment(res.data?.pace ?? []));
          setStrideLength(sliceForRelevantMoment(res.data?.amplitude ?? []));
          setAllure(sliceForRelevantMoment(res.data?.allure ?? []));
          setLoCardio(
            sliceForRelevantMoment(
              res.data?.lo_cardio ?? res.data?.loCardio ?? [],
            ),
          );
        })
        .catch((err) => {
          console.log({ err });
        });
    }
  }, [
    exam?.training?.moments_transposed_file_url,
    exam?.training?.relevant_moments_index_start,
    exam?.training?.relevant_moments_index_end,
  ]);

  const isExamHasEcg = useMemo(() => {
    return !isEmpty(exam?.training_ecg_data);
  }, [exam?.training_ecg_data]);

  const filteredCheckPoints = useMemo(() => {
    let cps = exam?.training?.checkpoints;
    if (cps && cps.length > 0) {
      let checkpoints = uniqBy(
        cps.map((cp) => cp.checkpoint),
        "@id",
      );

      let checkPointsMin: CheckPoint[] = [];
      checkpoints.forEach((cp) => {
        let getDataBydId =
          cps?.filter((c) => c?.checkpoint?.["@id"] === cp?.["@id"]) ?? [];

        let data = minBy(getDataBydId, "index_moment");

        data && checkPointsMin.push(data);
      });
      return checkPointsMin;
    } else {
      return [];
    }
  }, [exam?.training?.checkpoints]);

  const sliceForRelevantMoment = (arr: number[]) => {
    let shouldSlice =
      exam?.training?.relevant_moments_index_start ||
      exam?.training?.relevant_moments_index_end;
    if (!shouldSlice) return arr;

    return arr.slice(
      exam?.training.relevant_moments_index_start ?? 0,
      exam?.training.relevant_moments_index_end ?? arr.length,
    );
  };

  return (
    <LightThemePage>
      <Container maxWidth="lg" sx={{ mt: 5 }}>
        <Header
          boxProps={{
            justifyContent: { md: "flex-start", xs: "center" },
            alignItems: { md: "center", xs: "center" },
            width: { md: "auto", xs: "100%" },
          }}
          titleProps={
            !RESTRICTED
              ? {
                  component: "a",
                  target: "_blank",
                  href: routes.DASHBOARD_ROUTES.HORSE_VIEW.replace(
                    ":id",
                    String(exam?.training?.horse_trainer?.horse?.id),
                  ),
                }
              : {}
          }
          leftChildren={
            <img
              src={exam?.training?.horse_trainer?.horse?.avatar_url}
              alt="horse profile"
              width={85}
              height={85}
              style={{ borderRadius: "50%", objectFit: "cover" }}
            />
          }
          text={
            exam?.training?.horse_trainer?.horse?.name ?? "navbar:myTrainings"
          }
          date={`${formatDate(exam?.training?.creation_date ?? "", "ddd MMM DD YYYY - LT")}`}
          dateStyle={{
            fontSize: "17px",
          }}
        ></Header>

        <Divider sx={{ my: 2 }}></Divider>

        {examHasError && (
          <Box
            display={"flex"}
            justifyContent={"center"}
            alignItems={"center"}
            height={"calc(100vh - 200px)"}
          >
            <NotFound></NotFound>
          </Box>
        )}
        {isExamLoading || transposedMutation.isLoading || isUserFetching ? (
          <TrackViewSkeleton></TrackViewSkeleton>
        ) : (
          <Stack direction={"column"} spacing={3} pb={3}>
            <InformationSection
              pitchTemperature={
                auth?.type === "POLO" ? exam?.training?.pitch_temperature : null
              }
              humidity={
                auth?.type === "POLO" ? exam?.training?.pitch_humidity : null
              }
              printable
              restrictedMode={RESTRICTED}
              examId={exam?.training?.id}
              horse={exam?.training?.horse_trainer?.horse ?? null}
              rider={exam?.training?.rider?.name ?? null}
              intensity={exam?.training?.intensity ?? 0}
              duration={toNumber(exam?.training?.insight?.duration ?? 0)}
              trainingType={exam?.training?.training_type?.display_name ?? null}
              weather={exam?.training?.weather}
              horseShoe={exam?.training?.horse_shoeing?.display_name}
              track_name_auto={
                exam?.training?.track_name_automatic?.display_name
              }
              track_surface_auto={
                exam?.training?.track_surface_automatic?.display_name
              }
              training_type_auto={
                exam?.training?.training_type_automatic?.display_name
              }
              track={{
                id: exam?.training?.track_name?.id,
                name:
                  exam?.training?.track_name?.name ??
                  exam?.training?.track_name_automatic?.name,
                trackCondition: exam?.training?.track_condition?.display_name,
                trackSurfaceKey:
                  exam?.training?.track_surface?.name ??
                  exam?.training?.track_surface_automatic?.name,
                trackSurfaceName:
                  exam?.training?.track_surface?.display_name ??
                  exam?.training?.track_surface_automatic?.display_name,
              }}
              insight={exam?.training?.insight}
              isExamHasEcg={isExamHasEcg}
              device={
                exam?.training?.equimetre?.display_name ??
                exam?.training?.equimetre?.mac_address?.mac_address
              }
              date={formatDate(
                exam?.training?.creation_date ?? "",
                "ddd MMM DD YYYY - LT",
              )}
              comment={exam?.training?.comment}
            ></InformationSection>

            <PrintPage>
              <KeyParamSection
                printable
                restrictedMode={RESTRICTED}
                keyParams={exam?.computed_key_parameters}
              />
            </PrintPage>

            <PrintPage>
              <GaitAndHrZone
                printable
                horseId={exam?.training?.horse_trainer?.horse?.id}
                maxHr={exam?.hr_max}
                maxHrRest={exam?.training?.horse_computed_heart_rate_at_rest}
                data={exam?.averages_and_hr_zones}
              />
            </PrintPage>

            {isGMapLoaded && (
              <PrintPage>
                <MapSection
                  printable
                  enabled
                  setDistance={(value) => setDistance(value ?? "")}
                  transposedFile={exam?.training?.moments_transposed_file_url}
                  chronology={exam?.averages_and_hr_zones?.gait_chronology}
                  flIndex={exam?.training?.finish_line?.index_moment ?? null}
                  finishLine={exam?.training?.finish_line}
                  checkPoints={filteredCheckPoints}
                  onIndexChange={(value) => setCurrentindex(value)}
                  setIsLoading={(value) => setLoading(value)}
                  isFinishLineActive={isFinishLineActive}
                  enableCheckbox={false}
                  showSave={false}
                  speed={speed}
                  bpm={bpm}
                  lats={lats}
                  lngs={lngs}
                  distance={rawDistance}
                  time={time}
                  relevant_index_start={
                    exam?.training?.relevant_moments_index_start
                  }
                  relevant_index_end={
                    exam?.training?.relevant_moments_index_end
                  }
                  currentIndex={currentIndex}
                  allure={allure}
                  pace={strideFrequency}
                  strideLength={strideLength}
                />
              </PrintPage>
            )}

            <ChartsSection
              userType={auth?.type}
              chukkers={auth?.type === "POLO" ? chukkers : undefined}
              showIntervalTraining={
                exam?.training?.is_interval_training ?? false
              }
              loCardio={loCardio}
              editable={false}
              strideFrequency={strideFrequency}
              strideLength={strideLength}
              time={time}
              rawDistance={rawDistance}
              speed={speed}
              bpm={bpm}
              distance={distance}
              scaleOption={scaleOption}
              onChangeScale={(val: Scale) => setScaleOption(val)}
              currentIndex={currentIndex}
              flIndex={exam?.training?.finish_line?.index_moment ?? null}
              loading={loading}
              heats={exam?.training?.heats ?? []}
              isFinishLineActive={isFinishLineActive}
              intervals={exam?.training?.sectionals ?? []}
              id={exam?.training?.id}
              percentageGoodCardio={exam?.training?.id ?? 100}
              onIndexChange={(index) => setCurrentindex(index)}
              restrictedMode={RESTRICTED}
              lebelsColor="#000"
              printable
            ></ChartsSection>

            {!isEmpty(exam?.training?.checkpoints ?? []) && (
              <Card>
                <CardContent>
                  <CheckpointsSection
                    onChangeScale={(val: Scale) => setScaleOption(val)}
                    scaleOption={scaleOption}
                    editable={false}
                    isFinishlineActive={isFinishLineActive}
                    flIndex={exam?.training?.finish_line?.index_moment}
                    restrictedMode={RESTRICTED}
                    checkPoints={exam?.training?.checkpoints ?? []}
                    time={time}
                    rawDistance={rawDistance}
                    speed={speed}
                    bpm={bpm}
                  ></CheckpointsSection>
                </CardContent>
              </Card>
            )}
          </Stack>
        )}
      </Container>
    </LightThemePage>
  );
}

export default ExternalTraining;
